'use client'

import { cn } from '@/lib/utils'
import { ArrowUp } from 'lucide-react'
import { useEffect, useState } from 'react'

/*
This React component creates a "scroll to top" button that becomes visible when the user scrolls down the page. 
It includes a smooth scroll effect when clicked.
*/

export default function ScrollToTop() {
  // State to manage the visibility of the "scroll to top" button
  const [visible, setVisible] = useState(false)

  // Function to scroll back to the top of the page smoothly
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  // Add a scroll event listener to track when to show or hide the button
  useEffect(() => {
    const handleScroll = () => {
      const scrolledFromTop = window.scrollY >= 200
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10

      setVisible(scrolledFromTop && !nearBottom)
    }

    window.addEventListener('scroll', handleScroll)
    // Initial check
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, []) // An empty dependency array ensures that this effect runs only once when the component is mounted.

  // Render the "scroll to top" button, which is visible or hidden based on the 'visible' state
  return (
    <>
      {visible && (
        <div
          className={cn(
            'fixed bottom-4 right-4 z-10 flex h-5 w-5 items-center justify-center rounded-lg bg-opacity-5 p-4 backdrop-blur-xs',
            visible ? 'visible' : 'hidden',
            'hover:scale-105',
            'transition-all duration-300 ease-in-out',
            'border border-gray-700 shadow-md'
          )}
        >
          <button
            aria-label='Scroll to Top'
            onClick={scrollToTop}
            className='bg-opacity-30'
          >
            <ArrowUp />
          </button>
        </div>
      )}
    </>
  )
}
